import {
  brandsOfEquity,
  type BrandEquity,
  GRADE_LABELS,
  type InspectionTable,
  SEVERITY_LABELS,
  SIMULATOR_GRADES,
  SIMULATOR_SEVERITIES,
  type SimulatorValues,
} from 'used-vehicle-engine';
import { numberOf, pts } from './format';

export interface ValuesPanelProps {
  values: SimulatorValues;
  /** Dotted paths that differ from the defaults; their cells are tinted. */
  changedPaths: Set<string>;
  /** Bumped on reset so the uncontrolled cells remount with the defaults. */
  seq: number;
  table: InspectionTable;
  onChange: (path: string, value: number | string) => void;
  onTableChange: (table: InspectionTable) => void;
  onReset: () => void;
}

const LRM = '‎';

const Head = ({ title, span = 3 }: { title: string; span?: number }) => (
  <tr className="head">
    <td colSpan={span}>{title}</td>
  </tr>
);

interface CellProps {
  path: string;
  value: number;
  seq: number;
  changed: boolean;
  unit: '%' | 'pts' | 'km' | 'JOD' | 'owners';
  min?: number;
  max?: number;
  step?: number;
  width?: number;
  label: string;
  onChange: (path: string, value: number) => void;
}

/** One editable figure. Percentages and points are typed as `2` and stored as `0.02`. */
const Cell = ({ path, value, seq, changed, unit, min, max, step, width, label, onChange }: CellProps) => {
  const fraction = unit === '%' || unit === 'pts';

  return (
    <td className={`n${changed ? ' changed' : ''}`}>
      <input
        key={`${seq}-${path}`}
        className="cell"
        type="number"
        min={min}
        max={max}
        step={step}
        defaultValue={fraction ? pts(value) : value}
        aria-label={label}
        style={width ? { width } : undefined}
        onChange={(event) => {
          const typed = numberOf(event.target.value);

          onChange(path, fraction ? typed / 100 : typed);
        }}
      />{' '}
      <small>{unit}</small>
    </td>
  );
};

export const ValuesPanel = ({ values, changedPaths, seq, table, onChange, onTableChange, onReset }: ValuesPanelProps) => {
  const pctRows = (key: 'usage' | 'specs' | 'agency' | 'service' | 'demand' | 'supply', title: string, label: string) => (
    <>
      <Head title={title} />
      {values[key].map((option, index) => {
        const path = `${key}.${index}.pct`;

        return (
          <tr key={option.key}>
            <td
              className="opt"
              colSpan={2}
            >
              {option.name}
            </td>
            <Cell
              path={path}
              value={option.pct}
              seq={seq}
              changed={changedPaths.has(path)}
              unit="%"
              min={-50}
              max={50}
              step={1}
              label={`${label} ${option.name} percent`}
              onChange={onChange}
            />
          </tr>
        );
      })}
    </>
  );

  const misc: { label: string; path: string; unit: 'km' | '%' | 'owners' | 'JOD'; step: number; value: number }[] = [
    { label: 'Mileage allowance per year', path: 'km.allowance', unit: 'km', step: 1000, value: values.km.allowance },
    { label: 'Excess mileage step', path: 'km.step', unit: 'km', step: 1000, value: values.km.step },
    { label: 'Penalty per started step', path: 'km.rate', unit: '%', step: 0.5, value: values.km.rate },
    { label: 'Owners allowed', path: 'owners.allowance', unit: 'owners', step: 1, value: values.owners.allowance },
    { label: 'Penalty per extra owner', path: 'owners.rate', unit: '%', step: 0.5, value: values.owners.rate },
    { label: 'Facelift deduction', path: 'facelift', unit: '%', step: 1, value: values.facelift },
    { label: 'Customs market impact', path: 'customs', unit: '%', step: 1, value: values.customs },
    { label: 'Rounding', path: 'rounding', unit: 'JOD', step: 50, value: values.rounding },
  ];

  return (
    <details>
      <summary>Values used in the arithmetic (editable · a changed figure is tinted and listed on the saved estimate)</summary>
      <div className="tv">
        <div>
          <h4>
            Brand equity and powertrain{' '}
            <span className="q">
              · extra loss added to the ladder over the first three years, in points (half in Year 1, 30% in Year 2, 20% in Year 3)
            </span>
          </h4>
          <div className="table-wrap">
            <table>
              <tbody>
                <Head title="Brand equity" />
                {values.brand.map((option, index) => {
                  const path = `brand.${index}.pts`;

                  return (
                    <tr key={option.key}>
                      <td className="opt">{option.name}</td>
                      <td className="ex">{brandsOfEquity(option.key as BrandEquity).join(', ')}</td>
                      <Cell
                        path={path}
                        value={option.pts}
                        seq={seq}
                        changed={changedPaths.has(path)}
                        unit="pts"
                        min={-20}
                        max={60}
                        step={1}
                        label={`brand equity ${option.name} points`}
                        onChange={onChange}
                      />
                    </tr>
                  );
                })}
                <Head title="Powertrain" />
                {values.fuel.map((option, index) => {
                  const path = `fuel.${index}.pts`;

                  return (
                    <tr key={option.key}>
                      <td className="opt">{option.name}</td>
                      <td className="ex">{option.ex ?? ''}</td>
                      <Cell
                        path={path}
                        value={option.pts}
                        seq={seq}
                        changed={changedPaths.has(path)}
                        unit="pts"
                        min={-20}
                        max={60}
                        step={1}
                        label={`powertrain ${option.name} points`}
                        onChange={onChange}
                      />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <h4 style={{ marginTop: 10 }}>Worst-case profile</h4>
          <div className="table-wrap">
            <table>
              <tbody>
                <tr>
                  <td className="opt">Chassis grade</td>
                  <td className={`n${changedPaths.has('worst.grade') ? ' changed' : ''}`}>
                    <select
                      className="cell"
                      value={values.worst.grade}
                      aria-label="worst-case grade"
                      onChange={(event) => onChange('worst.grade', event.target.value)}
                    >
                      {SIMULATOR_GRADES.map((grade) => (
                        <option
                          key={grade}
                          value={grade}
                        >
                          {GRADE_LABELS[grade].number} — {GRADE_LABELS[grade].arabic}
                          {LRM}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <td className="opt">All assessments</td>
                  <td className="n">
                    <select
                      className="cell"
                      value={values.worst.sev}
                      aria-label="worst-case severity"
                      onChange={(event) => onChange('worst.sev', event.target.value)}
                    >
                      {SIMULATOR_SEVERITIES.map((severity) => (
                        <option
                          key={severity}
                          value={severity}
                        >
                          {SEVERITY_LABELS[severity]}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <td className="opt">Service history</td>
                  <td className="n">
                    <select
                      className="cell"
                      value={values.worst.service}
                      aria-label="worst-case service history"
                      onChange={(event) => onChange('worst.service', event.target.value)}
                    >
                      {values.service.map((option) => (
                        <option
                          key={option.key}
                          value={option.key}
                        >
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <td className="opt">Driven per year</td>
                  <Cell
                    path="worst.kmPerYear"
                    value={values.worst.kmPerYear}
                    seq={seq}
                    changed={changedPaths.has('worst.kmPerYear')}
                    unit="km"
                    min={0}
                    step={5000}
                    width={82}
                    label="worst-case km per year"
                    onChange={onChange}
                  />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h4>
            Usage, specs, warranty, agency, service and market <span className="q">· one-off % on the value, at every age</span>
          </h4>
          <div className="table-wrap">
            <table>
              <tbody>
                {pctRows('usage', 'Usage', 'usage')}
                {pctRows('specs', 'Regional specs', 'regional specs')}
                <Head title="Warranty" />
                <tr>
                  <td
                    className="opt"
                    colSpan={2}
                  >
                    Warranty available
                  </td>
                  <Cell
                    path="warranty"
                    value={values.warranty}
                    seq={seq}
                    changed={changedPaths.has('warranty')}
                    unit="%"
                    min={-50}
                    max={50}
                    step={1}
                    label="warranty percent"
                    onChange={onChange}
                  />
                </tr>
                {pctRows('agency', 'Agency reputation', 'agency reputation')}
                {pctRows('service', 'Service history', 'service history')}
                {pctRows('demand', 'Demand for the model', 'demand')}
                {pctRows('supply', 'Market supply of the model', 'supply')}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h4>
            Chassis grade deduction <span className="q">· % of the value</span>
          </h4>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Grade</th>
                  <th>Standard</th>
                  <th>Luxury</th>
                </tr>
              </thead>
              <tbody>
                {SIMULATOR_GRADES.map((grade) => (
                  <tr key={grade}>
                    <td className="opt">
                      {GRADE_LABELS[grade].number} — {GRADE_LABELS[grade].arabic}
                      {LRM}
                    </td>
                    {(['standard', 'luxury'] as InspectionTable[]).map((kind) => {
                      const path = `grade.${kind}.${grade}`;

                      return (
                        <Cell
                          key={kind}
                          path={path}
                          value={values.grade[kind][grade]}
                          seq={seq}
                          changed={changedPaths.has(path)}
                          unit="%"
                          min={0}
                          max={90}
                          step={1}
                          label={`${grade} ${kind} percent`}
                          onChange={onChange}
                        />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h4 style={{ marginTop: 10 }}>
            Severity deduction <span className="q">· each of interior, body, engine, hybrid system, tyres</span>
          </h4>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Assessment</th>
                  <th>Deduction</th>
                </tr>
              </thead>
              <tbody>
                {SIMULATOR_SEVERITIES.map((severity) => {
                  const path = `sev.${severity}`;

                  return (
                    <tr key={severity}>
                      <td className="opt">{SEVERITY_LABELS[severity]}</td>
                      <Cell
                        path={path}
                        value={values.sev[severity]}
                        seq={seq}
                        changed={changedPaths.has(path)}
                        unit="%"
                        min={0}
                        max={50}
                        step={0.5}
                        label={`${SEVERITY_LABELS[severity]} percent`}
                        onChange={onChange}
                      />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h4>Mileage, owners, model status and tables</h4>
          <div className="table-wrap">
            <table>
              <tbody>
                {misc.map((row) => (
                  <tr key={row.path}>
                    <td className="opt">{row.label}</td>
                    <Cell
                      path={row.path}
                      value={row.value}
                      seq={seq}
                      changed={changedPaths.has(row.path)}
                      unit={row.unit}
                      min={0}
                      step={row.step}
                      width={row.unit === '%' ? 56 : 82}
                      label={row.label}
                      onChange={onChange}
                    />
                  </tr>
                ))}
                <tr>
                  <td className="opt">Inspection tables for this car</td>
                  <td className="n">
                    <select
                      className="cell"
                      value={table}
                      aria-label="inspection tables"
                      onChange={(event) => onTableChange(event.target.value as InspectionTable)}
                    >
                      <option value="standard">standard</option>
                      <option value="luxury">luxury</option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <p
        className="table-note"
        style={{ marginTop: 12 }}
      >
        Damage amounts follow the standard and luxury tables and are shown in the two damage lists. Spare key, manual, tool kit and spare tyre add-ons
        are left out. The trim level is recorded but, by request, changes nothing.
      </p>
      <button
        type="button"
        className="plain"
        style={{ marginTop: 12 }}
        onClick={onReset}
      >
        Reset to defaults
      </button>
    </details>
  );
};
